export enum Focus {
  First, // Focus the first non-disabled item
  Previous, // Focus the previous non-disabled item
  Next, // Focus the next non-disabled item
  Last, // Focus the last non-disabled item
  Specific, // Focus a specific item based on the id of the item
  Nothing, // Focus no items at all
}

type Actions =
  | { focus: Focus.Specific; id: string }
  | { focus: Exclude<Focus, Focus.Specific> }

interface Resolvers<Item> {
  resolveActiveIndex(): number | null
  resolveDisabled(item: Item): boolean
  resolveId(item: Item): string
  resolveItems(): Item[]
}

export function calculateActiveIndex<Item>(
  action: Actions,
  resolvers: Resolvers<Item>,
): number | null {
  const items = resolvers.resolveItems()

  if (items.length <= 0) {
    return null
  }

  const currentActiveIndex = resolvers.resolveActiveIndex()
  const activeIndex = currentActiveIndex ?? -1

  const nextActiveIndex = (() => {
    switch (action.focus) {
      case Focus.First:
        return items.findIndex((item) => !resolvers.resolveDisabled(item))
      case Focus.Previous: {
        const index = items
          .slice()
          .reverse()
          .findIndex((item, index, all) => {
            if (activeIndex !== -1 && all.length - index - 1 >= activeIndex) {
              return false
            }

            return !resolvers.resolveDisabled(item)
          })

        if (index === -1) {
          return currentActiveIndex === 0 ? items.length - 2 - index : index
        }

        return items.length - 1 - index
      }
      case Focus.Next: {
        if (currentActiveIndex === items.length - 1) {
          return items.findIndex((item) => !resolvers.resolveDisabled(item))
        }

        return items.findIndex((item, index) => {
          if (index <= activeIndex) {
            return false
          }

          return !resolvers.resolveDisabled(item)
        })
      }
      case Focus.Last: {
        const index = items
          .slice()
          .reverse()
          .findIndex((item) => !resolvers.resolveDisabled(item))

        if (index === -1) {
          return index
        }

        return items.length - 1 - index
      }
      case Focus.Specific:
        return items.findIndex(
          (item) => resolvers.resolveId(item) === action.id,
        )

      case Focus.Nothing:
        return null
    }
  })()

  return nextActiveIndex === -1 ? currentActiveIndex : nextActiveIndex
}
